import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import type { QueryFilter, Lab } from '@mariana/labos-client';
import { limsClient } from './api/limsClient';

export type LabSelection = number;

const HQ_LAB_ID = 1;

const ENTITY_TYPES_WITH_LAB = new Set(['sample', 'test', 'order']);
export const entityHasLabId = (type: string): boolean => ENTITY_TYPES_WITH_LAB.has(type);

interface LabContextValue {
  labs: Lab[];
  labSelection: LabSelection;
  setLabSelection: (selection: LabSelection) => void;
  labFilters: QueryFilter[];
  /** lab_id value for event log queries: number for a specific lab, null for HQ (IS NULL) */
  eventLabId: number | null;
}

const LabContext = createContext<LabContextValue>({
  labs: [],
  labSelection: HQ_LAB_ID,
  setLabSelection: () => {},
  labFilters: [],
  eventLabId: null,
});

const STORAGE_KEY = 'lims-lab-selection';

function getInitialLabSelection(): LabSelection {
  const urlLab = new URLSearchParams(window.location.search).get('lab');
  if (urlLab) {
    const id = Number(urlLab);
    if (!isNaN(id) && id > 0) return id;
  }
  const stored = localStorage.getItem(STORAGE_KEY);
  const storedId = stored ? Number(stored) : NaN;
  return !isNaN(storedId) && storedId > 0 ? storedId : HQ_LAB_ID;
}

function syncLabToURL(labId: LabSelection) {
  const url = new URL(window.location.href);
  url.searchParams.set('lab', String(labId));
  window.history.replaceState(null, '', url.toString());
}

export function LabProvider({ children }: { children: React.ReactNode }) {
  const [labs, setLabs] = useState<Lab[]>([]);
  const [labSelection, setLabSelectionState] = useState<LabSelection>(getInitialLabSelection);

  useEffect(() => {
    let cancelled = false;
    limsClient.listEntitiesByType('lab', { sort_by: 'id', sort_order: 'asc' }).then(res => {
      if (cancelled) return;
      const fetched = res.data ?? [];
      setLabs(fetched as Lab[]);

      const stored = localStorage.getItem(STORAGE_KEY);
      const storedId = stored ? Number(stored) : NaN;
      const validIds = new Set(fetched.map(l => l.id));
      if (!isNaN(storedId) && validIds.has(storedId)) {
        setLabSelectionState(storedId);
        syncLabToURL(storedId);
      } else if (fetched.length > 0) {
        setLabSelectionState(fetched[0].id!);
        syncLabToURL(fetched[0].id!);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    syncLabToURL(labSelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.dataset.lab = String(labSelection);
  }, [labSelection]);

  const setLabSelection = (selection: LabSelection) => {
    setLabSelectionState(selection);
    localStorage.setItem(STORAGE_KEY, String(selection));
    syncLabToURL(selection);
  };

  const labFilters = useMemo<QueryFilter[]>(() => {
    if (labSelection === HQ_LAB_ID) {
      return [{ attribute_name: 'lab_id', operator: 'IS NULL' }];
    }
    return [{ attribute_name: 'lab_id', operator: '=', value: labSelection }];
  }, [labSelection]);

  const eventLabId = labSelection === HQ_LAB_ID ? null : labSelection;

  return (
    <LabContext.Provider value={{ labs, labSelection, setLabSelection, labFilters, eventLabId }}>
      {children}
    </LabContext.Provider>
  );
}

export const useLab = () => useContext(LabContext);
